import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, List, Row, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { toUploadStatusColor } from "../../constants";
import { File as ApiFile, SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useEffect, useRef, useState } from "react";
import Dragger from "antd/es/upload/Dragger";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { AssetUploadStatuses, WorkOrder } from "../../api/models";

type WorkOrderUploadParametersProps = {
  workOrder?: WorkOrder,
  onClose: () => void,
  onSave: (workOrder: WorkOrder) => void
};

type WorkOrderParameterItem = {
  path: string,
  type: "File" | "Directory",
  uploaded: boolean
};

type WorkOrderParameterItems = {
  [path: string]: WorkOrderParameterItem
};

function createParameterItems(assetUploadStatuses?: AssetUploadStatuses): WorkOrderParameterItems {
  const items: WorkOrderParameterItems = {};
  for (const file of assetUploadStatuses?.files ?? []) {
    items[file.path] = {
      path: file.path,
      type: "File",
      uploaded: file.ready
    };
  }
  for (const directory of assetUploadStatuses?.directories ?? []) {
    items[directory.directory] = {
      path: directory.directory,
      type: "Directory",
      uploaded: directory.ready
    };
  }

  return items;
}

export default function (p: WorkOrderUploadParametersProps) {
  const [items, setItems] = useState<WorkOrderParameterItems>({});
  const [assetInitializationStatus, setAssetInitializationStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [assetSubmitStatus, setAssetSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [confirmSubmitStatus, setConfirmSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [error, setError] = useState<string>();
  const [activeItem, setActiveItem] = useState<WorkOrderParameterItem>();
  const singleFileInputRef = useRef<HTMLInputElement>(null);
  const directoryFilesInputRef = useRef<HTMLInputElement>(null);
  const { api } = useContext(ApiContext);
  const isInitializing = assetInitializationStatus === SubmitStatus.Submitting;
  const isAssetSubmitting = assetSubmitStatus === SubmitStatus.Submitting;
  const isConfirmSubmitting = confirmSubmitStatus === SubmitStatus.Submitting;
  const canConfirm = !isInitializing &&
    !isAssetSubmitting &&
    !isConfirmSubmitting &&
    Object.values(items).every(i => i.uploaded);
  const canUploadItem = !isInitializing && !isAssetSubmitting && !isConfirmSubmitting;

  const uploadFiles = (filesToUpload: ApiFile[]) => {
    if (filesToUpload.length === 0) {
      return;
    }

    setAssetSubmitStatus(SubmitStatus.Submitting);
    api?.postWorkOrderParameterAssets(p.workOrder?.workOrderID!, filesToUpload).then(response => {
      if (!response.good() || !response.body) {
        setError("Failed to upload the parameter(s).");
        return;
      }

      setItems(createParameterItems(response.body));
      setError(undefined);
    }).catch(_ => {
      setError("Failed to upload the parameter(s).");
    }).finally(() => {
      setAssetSubmitStatus(SubmitStatus.Idle);
    });
  };

  const isZipFile = (fileName: string) => fileName.toLowerCase().endsWith(".zip");

  const onItemTagClicked = (item: WorkOrderParameterItem) => {
    if (!canUploadItem) {
      return;
    }

    setActiveItem(item);
    if (item.type === "File") {
      singleFileInputRef.current!.value = "";
      singleFileInputRef.current!.click();
    } else {
      directoryFilesInputRef.current!.value = "";
      directoryFilesInputRef.current!.click();
    }
  };

  // Load the initial status of the work order's parameters.
  useEffect(() => {
    api?.getWorkOrderParameterAssets(p.workOrder?.workOrderID!).then(response => {
      if (!response.good()) {
        setError("Failed to get the work order parameters.");
        return;
      }

      setItems(createParameterItems(response.body!));
    }).catch(() => {
      setError("Failed to get the work order parameters.");
    }).finally(() => {
      setAssetInitializationStatus(SubmitStatus.Idle);
    });
  }, []);

  return (
    <>
      <Title style={titleStyle} level={4}>Upload Parameters</Title>
      <Form
        layout="vertical"
        colon={false}>
        <Form.Item label="Name">
          <Paragraph strong>{p.workOrder?.name}</Paragraph>
        </Form.Item>
        <Form.Item label="Render Count">
          <Paragraph strong>{p.workOrder?.renderCount}</Paragraph>
        </Form.Item>
        <Form.Item label="Upload Parameters">
          <Dragger
            name="file"
            listType={undefined}
            multiple={true}
            action={undefined}
            showUploadList={false}
            fileList={[]}
            beforeUpload={() => false}
            disabled={false}
            onChange={e => {
              for (const file of e.fileList) {
                if (!file.originFileObj) {
                  setError(`File '${file.name}' could not be read.`);
                  return;
                }
              }

              const filesToUpload: ApiFile[] = [];
              for (const file of e.fileList) {
                const isZip = isZipFile(file.name);
                const isKnownFile = items[file.name]?.type === "File";
                if (!isZip && !isKnownFile) {
                  continue;
                }

                filesToUpload.push({
                  fileName: file.name,
                  fileData: file.originFileObj!
                });
              }

              uploadFiles(filesToUpload);
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to this area to upload.</p>
          </Dragger>
        </Form.Item>
        <Form.Item label="Parameters">
          <List
            bordered={false}
            footer={false}
            loading={isInitializing}
            pagination={{ pageSize: 5, hideOnSinglePage: Object.keys(items).length < 6 }}
            size="small"
            dataSource={Object.values(items)}
            renderItem={(item: WorkOrderParameterItem) =>
              <List.Item key={item.path}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{item.path}</span>
                  <Tag
                    color={toUploadStatusColor(item.uploaded)}
                    style={{ cursor: canUploadItem ? "pointer" : "default" }}
                    onClick={() => onItemTagClicked(item)}>
                    {item.uploaded ? "Uploaded" : "Not Uploaded"}
                  </Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
          <input
            ref={singleFileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file || !activeItem || isZipFile(file.name)) {
                setActiveItem(undefined);
                return;
              }

              uploadFiles([{ fileName: activeItem.path, fileData: file }]);
              setActiveItem(undefined);
            }}
          />
          <input
            ref={directoryFilesInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={e => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (!activeItem) {
                return;
              }

              const filesToUpload = files
                .filter(file => !isZipFile(file.name))
                .map(file => ({ fileName: `${activeItem.path}/${file.name}`, fileData: file }));

              uploadFiles(filesToUpload);
              setActiveItem(undefined);
            }}
          />
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            disabled={isAssetSubmitting || isConfirmSubmitting}
            onClick={p.onClose}>
            Close
          </Button>
          <Button
            type="primary"
            style={buttonStyle}
            disabled={!canConfirm}
            loading={isConfirmSubmitting}
            onClick={() => {
              setConfirmSubmitStatus(SubmitStatus.Submitting);
              api?.postWorkOrderParameterConfirm(p.workOrder?.workOrderID!).then(response => {
                if (!response.good()) {
                  setError("Failed to confirm the parameters.");
                  return;
                }

                return api?.getWorkOrder(p.workOrder?.workOrderID!).then(workOrderResponse => {
                  if (!workOrderResponse.good() || !workOrderResponse.body) {
                    setError("Failed to get the work order.");
                    return;
                  }

                  p.onSave(workOrderResponse.body);
                });
              }).catch(() => {
                setError("Failed to confirm the parameters.");
              }).finally(() => {
                setConfirmSubmitStatus(SubmitStatus.Idle);
              });
            }}>
            Confirm
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}
